import React from "react";
import numeral from "numeral";
import Decimal from "decimal.js";
import store from "../../store";

import BootstrapTooltip from "../../common/toolTip";
import { EPSILON } from "../numberConstants";
import { createDecimal } from "./math";

// Units        |       SubUnits
// Dollar       |       Cent
// Pound        |       Pence
// Saudi Riyal  |       Halalah

/**
 * Converts a subunit value (e.g., cents, pence) to its corresponding main unit (e.g., dollars, pounds).
 *
 * @param {number} num - Value in subunits.
 * @returns {number} - Value in main units.
 */
export const calculateSubunitToUnit = (num) => {
  const parsedNum = createDecimal(num);

  if (parsedNum.equals(0)) {
    return 0;
  }

  // Normalize small values to 0
  // const normalizedNum = Math.abs(parsedNum) <= EPSILON ? 0 : parsedNum;
  const normalizedNum = parsedNum.abs().lte(EPSILON)
    ? new Decimal(0)
    : parsedNum;

  // Convert subunit to unit
  return normalizedNum.div(100).toNumber();
};

/**
 * Converts a main unit value (e.g., dollars, pounds) to its corresponding subunits (e.g., cents, pence).
 * Uses `Math.round` to maintain precision and avoid floating-point errors.
 *
 * @param {number} num - Value in main units.
 * @returns {number} - Value in subunits.
 */
export const calculateUnitToSubunit = (num) => {
  const number = createDecimal(num);

  if (number.equals(0)) {
    return 0;
  }

  return number.mul(100).round().toNumber();
};

/**
 * Converts a numeric value to a formatted currency string in main units.
 * Includes optional symbol, decimal dropping, and number shortening.
 *
 * @param {number} num - The value to be formatted.
 * @param {boolean} hasSymbol - Whether to include the currency symbol.
 * @param {boolean} dropDecimal - Whether to remove decimal points.
 * @param {boolean} moduleShorten - Whether to shorten large numbers (e.g., millions).
 * @returns {string} - Formatted currency string.
 */
export const convertToCurrencyUnit = (
  num,
  hasSymbol = true,
  dropDecimal = false,
  moduleShorten = false,
) => {
  let symbol = hasSymbol
    ? store.getState().getIn(["config", "accountConfig", "currency", "title"])
    : "";
  let direction = store
    .getState()
    .getIn(["config", "accountConfig", "direction", "value"]);
  let enableCurrencyShorten = store
    .getState()
    .getIn(["config", "accountConfig", "enable_currency_shorten"]);
  let enableHeaderCurrency = store
    .getState()
    .getIn(["config", "accountConfig", "enable_header_currency"]);
  let number = createDecimal(num);
  let format = "";

  // Only numbers millions should be shortened
  if (
    moduleShorten &&
    enableCurrencyShorten &&
    (number.greaterThan(new Decimal(999999)) ||
      number.lessThan(new Decimal(-999999)))
  ) {
    // When shortening the currency
    format = new Intl.NumberFormat("en", {
      notation: "compact",
      minimumFractionDigits: dropDecimal ? 0 : 2,
    }).format(number);
  } else {
    // When not shortening the currency
    format = numeral(number).format(`0,0${dropDecimal ? "" : ".00"}`);
  }

  if (!hasSymbol) {
    return format;
  }

  let space = enableHeaderCurrency && symbol?.length > 1 ? " " : "";

  return symbol
    ? direction === "RTL"
      ? format + space + symbol
      : symbol + space + format
    : format;
};

/**
 * Formats a numeric value in subunits to a readable currency format in main units.
 * Supports optional symbol inclusion, decimal dropping, and bracket formatting for negatives.
 *
 * @param {number} num - Value in subunits.
 * @param {boolean} hasSymbol - Whether to include the currency symbol.
 * @param {boolean} dropDecimal - Whether to remove decimal points.
 * @param {boolean} moduleShorten - Whether to shorten large numbers.
 * @param {boolean} bracketFormat - Whether to format negatives with brackets.
 * @returns {string} - Formatted currency string.
 */
export const currencySubunitToUnit = (
  num,
  hasSymbol = true,
  dropDecimal = false,
  moduleShorten = false,
  bracketFormat = false,
) => {
  let symbol = hasSymbol
    ? store.getState().getIn(["config", "accountConfig", "currency", "title"])
    : "";
  let direction = store
    .getState()
    .getIn(["config", "accountConfig", "direction", "value"]);
  let enableCurrencyShorten = store
    .getState()
    .getIn(["config", "accountConfig", "enable_currency_shorten"]);
  let enableHeaderCurrency = store
    .getState()
    .getIn(["config", "accountConfig", "enable_header_currency"]);

  let number = calculateSubunitToUnit(num);
  number = createDecimal(number);
  let format = "";
  // Only numbers millions should be shortened
  if (
    moduleShorten &&
    enableCurrencyShorten &&
    (number.greaterThan(new Decimal(999999)) ||
      number.lessThan(new Decimal(-999999)))
  ) {
    // When shortening the currency
    format = new Intl.NumberFormat("en", {
      notation: "compact",
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(number);
  } else {
    // When not shortening the currency
    format = numeral(number).format(`0,0${dropDecimal ? "" : ".00"}`);
  }

  if (bracketFormat && format.startsWith("-")) {
    // Format negative values to brackets.
    format = `(${format.slice(1)})`;
  }

  if (!hasSymbol) {
    return format;
  }

  let space = enableHeaderCurrency && symbol?.length > 1 ? " " : "";

  return symbol
    ? direction === "RTL"
      ? format + space + symbol
      : symbol + space + format
    : format;
};

/**
 * Formats large currency values and optionally wraps them in a tooltip.
 *
 * @param {number} num - Numeric value in subunits.
 * @param {boolean} showTooltip - Whether to display the full value in a tooltip.
 * @returns {JSX.Element|string} - JSX element with tooltip or formatted string.
 */
export const formatLargeCurrency = (num, showTooltip = false) => {
  const accountConfig = store.getState().getIn(["config", "accountConfig"]);

  let displaygorssItem;
  const number = createDecimal(num);
  if (number.gt(new Decimal("100_000_000_000"))) {
    // Convert the number to a string
    const numberString = num.toString();

    // Get the integer part of the number
    const integerPart = numberString.split(".")[0];

    // Calculate the number of characters before the ellipsis
    const charactersBeforeEllipsis = integerPart.length - 5; // Keep two decimal places

    // Create a pattern for the regular expression
    const pattern = new RegExp(`^(\\d{${charactersBeforeEllipsis}})\\d*`);

    // Extract and format the number part before the ellipsis
    const formattedNumber = integerPart.replace(pattern, "$1");

    // Add commas to the formatted number
    const numberWithCommas = formattedNumber.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      ",",
    );

    if (showTooltip) {
      // eslint-disable-next-line
      displaygorssItem = (
        <BootstrapTooltip
          className="amount-tooltip"
          title={currencySubunitToUnit(num, true, false, false)}
          placement="bottom"
        >
          <div>
            {!accountConfig?.get("enable_header_currency") &&
              accountConfig?.getIn(["currency", "title"])}
            {numberWithCommas + "..."}
          </div>
        </BootstrapTooltip>
      );
    } else {
      displaygorssItem = (
        <>
          {!accountConfig?.get("enable_header_currency") &&
            accountConfig?.getIn(["currency", "title"])}
          {numberWithCommas + "..."}
        </>
      );
    }
  } else {
    displaygorssItem = currencySubunitToUnit(
      num,
      !accountConfig?.get("enable_header_currency"),
      false,
      false,
    );
  }
  return displaygorssItem;
};
